import React, { useState } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageSubtitle, PageTitle } from "../Layouts/components/PageTitleContainer";
import { Stack, Button, Select, MenuItem, Box, SelectChangeEvent, TextField } from "@mui/material";
import { MovementAction } from "types/locationManagement/movementAction";
import { LocationManagementLocationType } from "types/locationManagement/locationType";
import { FieldControl } from "components/Form/FieldControl";
import { Form } from "components/Form/Form";

const MovementActionForm: React.FC = () => {
  const { movementActionId } = useParams<{ movementActionId?: string }>();
  const isEdit = Boolean(movementActionId);
  const { action, locationTypes } = useLoaderData() as {
    action: MovementAction;
    locationTypes: LocationManagementLocationType[];
  };

  const [inventoryAttribute, setInventoryAttribute] = useState<string>(action.inventoryAttribute || "");
  const [modificationType, setModificationType] = useState<string>(action.modificationType || "");

  const handleInventoryAttributeChange = (e: SelectChangeEvent<string>) => {
    setInventoryAttribute(e.target.value);
    if (e.target.value === "state") {
      setModificationType("set");
    }
  };

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs
        links={[
          { label: "Admin", path: "/" },
          { label: "Movement Actions", path: "/movement-actions" },
        ]}
      />
      <PageTitle>{isEdit ? "Edit" : "Create"} Action</PageTitle>
      <PageSubtitle>
        Create an action that occurs when inventory move between location types. Actions can only be made at the
        location type level and must include a valid location type.
      </PageSubtitle>
      <Box sx={{ m: 3 }}>
        <Form method={isEdit ? "PUT" : "POST"} resource="movementAction">
          <Stack direction="column" spacing={2}>
            <FieldControl fullWidth name={"fromLocationTypeId"} label="From Location Type">
              {({ name, label, defaultValue, labelId }) => (
                <Select name={name} label={label} defaultValue={defaultValue} labelId={labelId} id={`${name}-select`}>
                  {locationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            <FieldControl fullWidth required name={"toLocationTypeId"} label="To Location Type">
              {({ name, label, defaultValue, labelId }) => (
                <Select name={name} label={label} defaultValue={defaultValue} labelId={labelId} id={`${name}-select`}>
                  {locationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            <FieldControl
              fullWidth
              required
              name={"inventoryAttribute"}
              label="Inventory Attribute"
              helperText="If you don’t see the attribute you’d like to change, reach out to the engineering team for help."
            >
              {({ name, label, defaultValue, labelId }) => (
                <Select
                  labelId={labelId}
                  id={`${name}-select`}
                  name={name}
                  defaultValue={defaultValue || ""}
                  label={label}
                  onChange={handleInventoryAttributeChange}
                >
                  <MenuItem value="warehouse_location">Warehouse Location</MenuItem>
                  <MenuItem value="state">State</MenuItem>
                  <MenuItem value="condition">Condition</MenuItem>
                </Select>
              )}
            </FieldControl>
            <FieldControl fullWidth required name={"modificationType"} label="Modification Type">
              {({ name, label, defaultValue, labelId }) => (
                <Select
                  labelId={labelId}
                  id={`${name}-select`}
                  name={name}
                  defaultValue={defaultValue || ""}
                  label={label}
                  onChange={(e) => setModificationType(e.target.value)}
                  value={modificationType}
                >
                  <MenuItem value="set">Set Value</MenuItem>
                  {inventoryAttribute !== "state" && <MenuItem value="set_nil">Clear Value</MenuItem>}
                </Select>
              )}
            </FieldControl>
            {inventoryAttribute === "state" && (
              <FieldControl fullWidth required name={"modificationValue"} label="Modification Value">
                {({ name, label, defaultValue, labelId }) => (
                  <Select
                    labelId={labelId}
                    id={`${name}-select`}
                    name={name}
                    defaultValue={defaultValue || ""}
                    label={label}
                  >
                    <MenuItem value="resellable">Resellable</MenuItem>
                    <MenuItem value="in_process">In Process</MenuItem>
                    <MenuItem value="ewasted">Ewasted</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                    <MenuItem value="invoiced">Invoiced</MenuItem>
                    <MenuItem value="reserved">Reserved</MenuItem>
                    <MenuItem value="missing">Missing</MenuItem>
                    <MenuItem value="pending_review">Pending Review</MenuItem>
                    <MenuItem value="wip">WIP</MenuItem>
                    <MenuItem value="audit">Audit</MenuItem>
                    <MenuItem value="return">Return</MenuItem>
                    <MenuItem value="unfulfilled">Unfulfilled</MenuItem>
                    <MenuItem value="shipped">Shipped</MenuItem>
                    <MenuItem value="receiving">Receiving</MenuItem>
                    <MenuItem value="tech">Tech</MenuItem>
                    <MenuItem value="resale">Resale</MenuItem>
                    <MenuItem value="deployable">Deployable</MenuItem>
                    <MenuItem value="deployed">Deployed</MenuItem>
                    <MenuItem value="retired">Retired</MenuItem>
                  </Select>
                )}
              </FieldControl>
            )}
            {inventoryAttribute !== "state" && (
              <FieldControl
                fullWidth
                label="Modification Value"
                required
                name={"modificationValue"}
                component={TextField}
                defaultValue={action.modificationValue}
              />
            )}
            <Button type="submit" variant="contained">
              {isEdit ? "Update" : "Create"}
            </Button>
          </Stack>
        </Form>
      </Box>
    </FullPagePaperLayout>
  );
};

export default MovementActionForm;
